import { ExportEmailType, IBaseExport } from '@/models/downloadsExports'
import { ExportStatus } from '@/models/DTO/ExportTaskDTO'
import { downloadFile, getExportTaskById } from '@/services/exportDataService'
import { useLanguageStore } from '@/stores/LanguageStore'
import { AxiosResponse } from 'axios'
import { defineStore } from 'pinia'
import { useToast } from 'primevue'
import { ref } from 'vue'

import { useSignalRStore } from './SignalRStore'

export const useExportDataStore = defineStore('exportData', () => {
  const toast = useToast()
  const signalRStore = useSignalRStore()
  const languageStore = useLanguageStore()

  const downloadTaskId = ref('')
  const isLoadingExportData = ref(false)
  const selectedEmailOption = ref<ExportEmailType>(ExportEmailType.withoutEmail)
  const trackedTaskIds = new Set<string>()
  const finishedTaskIds = new Set<string>()
  const pollingTimers = new Map<string, ReturnType<typeof setTimeout>>()
  const pollingAttempts = new Map<string, number>()
  const finishedTaskCleanupTimers = new Map<string, ReturnType<typeof setTimeout>>()
  const finishedTaskTtlMs = 60_000
  const pollingIntervalMs = 5000
  const maxPollingAttempts = 60

  const clearPolling = (taskId: string) => {
    const timer = pollingTimers.get(taskId)

    if (timer) {
      clearTimeout(timer)
      pollingTimers.delete(taskId)
    }
  }

  const clearFinishedTaskCleanup = (taskId: string) => {
    const timer = finishedTaskCleanupTimers.get(taskId)

    if (timer) {
      clearTimeout(timer)
      finishedTaskCleanupTimers.delete(taskId)
    }
  }

  const scheduleFinishedTaskCleanup = (taskId: string) => {
    clearFinishedTaskCleanup(taskId)

    finishedTaskCleanupTimers.set(
      taskId,
      setTimeout(() => {
        finishedTaskIds.delete(taskId)
        finishedTaskCleanupTimers.delete(taskId)
      }, finishedTaskTtlMs),
    )
  }

  const handleTaskFinished = (taskId: string, status: string, notificationContent?: string) => {
    if (!trackedTaskIds.has(taskId) || finishedTaskIds.has(taskId)) {
      return
    }

    clearPolling(taskId)
    pollingAttempts.delete(taskId)
    finishedTaskIds.add(taskId)
    trackedTaskIds.delete(taskId)
    scheduleFinishedTaskCleanup(taskId)

    if (status === 'success') {
      toast.add({
        severity: 'success',
        summary: languageStore.t('views.downloads.exportCompleted'),
        detail: languageStore.t('views.downloads.exportCompletedDetail'),
        life: 3000,
      })
      return
    }

    toast.add({
      severity: 'error',
      summary: languageStore.t('views.downloads.exportError'),
      detail: notificationContent || languageStore.t('views.downloads.exportErrorDefault'),
      life: 5000,
    })
  }

  const scheduleTaskPolling = (taskId: string, delayMs = pollingIntervalMs) => {
    if (!trackedTaskIds.has(taskId) || finishedTaskIds.has(taskId)) {
      clearPolling(taskId)
      pollingAttempts.delete(taskId)
      return
    }

    const attempts = (pollingAttempts.get(taskId) ?? 0) + 1
    pollingAttempts.set(taskId, attempts)

    if (attempts > maxPollingAttempts) {
      clearPolling(taskId)
      pollingAttempts.delete(taskId)

      toast.add({
        severity: 'info',
        summary: languageStore.t('views.downloads.exportDelayed'),
        detail: languageStore.t('views.downloads.exportDelayedDetail'),
        life: 6000,
      })
      return
    }

    clearPolling(taskId)

    pollingTimers.set(
      taskId,
      setTimeout(async () => {
        if (!trackedTaskIds.has(taskId) || finishedTaskIds.has(taskId)) {
          clearPolling(taskId)
          return
        }

        try {
          const response = await getExportTaskById(taskId)
          const status = response.data?.status

          if (status === ExportStatus.Completed) {
            handleTaskFinished(taskId, 'success')
            return
          }

          if (status === ExportStatus.Failed) {
            handleTaskFinished(taskId, 'error')
            return
          }
        } catch (error) {
          console.error('Erro ao consultar status da exportação:', error)
        }

        scheduleTaskPolling(taskId)
      }, delayMs),
    )
  }

  const setEmailOption = (option: IBaseExport) => {
    selectedEmailOption.value = option.requiresEmailNotification
      ? ExportEmailType.withEmail
      : ExportEmailType.withoutEmail
  }

  const setupHubConnection = async () => {
    if (!signalRStore.hubConnection) {
      toast.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível estabelecer a conexão com o hub',
        life: 3000,
      })
      return
    }

    signalRStore.hubConnection?.off('DownloadNotification')

    signalRStore.hubConnection?.on(
      'DownloadNotification',
      async (taskId: string, status: string, notificationContent: string) => {
        console.log('DownloadNotification', taskId, status, notificationContent)
        if (status === 'success') {
          handleTaskFinished(taskId, status, notificationContent)
        }

        if (status === 'error') {
          handleTaskFinished(taskId, status, notificationContent)
        }
      },
    )
  }

  const setupDownload = async <T extends IBaseExport>(
    exportCallApi: (params: T) => Promise<
      AxiosResponse<{
        taskId: string
        message: string
      }>
    >,
    params: T,
  ) => {
    isLoadingExportData.value = true

    setEmailOption(params)

    if (!signalRStore.connectionId) {
      await signalRStore.startConnection()
      await setupHubConnection()
    }

    if (!signalRStore.connectionId) {
      toast.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível estabelecer a conexão com o hub',
        life: 3000,
      })
      isLoadingExportData.value = false
      throw new Error('SignalR connectionId is not available')
    }

    return exportCallApi({ ...params, connectionId: signalRStore.connectionId || undefined })
      .then((res) => {
        toast.add({
          summary: languageStore.t('views.downloads.exportStarted'),
          detail: res.data.message || languageStore.t('views.downloads.exportStartedDefault'),
          severity: 'success',
          life: 3000,
        })

        downloadTaskId.value = res.data.taskId
        trackedTaskIds.add(res.data.taskId)
        clearFinishedTaskCleanup(res.data.taskId)
        finishedTaskIds.delete(res.data.taskId)
        scheduleTaskPolling(res.data.taskId)
      })
      .catch((error) => {
        console.log(error)
        throw error
      })
      .finally(() => {
        isLoadingExportData.value = false
      })
  }

  const downloadProcessedFile = async (taskId: string, fileName: string) => {
    await downloadFile(taskId)
      .then((res) => {
        const dataType = res.data.type
        let binaryData = []

        binaryData.push(res.data)

        let downloadLink = document.createElement('a')
        downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }))
        downloadLink.setAttribute('download', fileName)
        document.body.appendChild(downloadLink)
        downloadLink.click()
      })
      .catch(() => {
        toast.add({
          summary: 'Erro',
          detail: 'Ocorreu um erro ao baixar o arquivo',
          severity: 'error',
        })
      })
  }

  return {
    isLoadingExportData,
    selectedEmailOption,
    downloadTaskId,
    setupDownload,
    setupHubConnection,
    downloadProcessedFile,
  }
})
